import { DocumentDownloadIcon } from '@heroicons/react/outline';
import { Button } from '@yoast/ui-library';
import { useState } from 'react';

import {
  useGetAnalysis,
  useGetBrands,
} from '../../../api/generated/insights';
import { isSpecificAnalysisId } from '../../../utils/isSpecificAnalysisId';
import { getAIProviderLabel } from '../../../utils/stringUtils';
import { useBrandAnalysisData } from '../../../contexts';
import useScan from '../../../hooks/useScan';
import { useToast } from '../../../hooks';
import { formatHumanDate, safeFilenamePart } from './utils';

interface DownloadPdfButtonProps {
  disabled?: boolean;
}

// `@react-pdf/renderer` depends on Node-style `Buffer` and `process` globals.
// Vite's lib build doesn't inject these polyfills automatically, and the host
// app (MyYoast) doesn't provide them either. We attach them lazily, only on
// the first PDF download, so the rest of the library has zero polyfill cost.
async function ensureNodeGlobalsForPdf(): Promise<void> {
  const g = globalThis as typeof globalThis & {
    Buffer?: unknown;
    process?: unknown;
  };
  if (!g.Buffer) {
    const { Buffer } = await import('buffer');
    g.Buffer = Buffer;
  }
  if (!g.process) {
    const processPolyfill = (await import('process')).default;
    g.process = processPolyfill;
  }
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Defer revoke to give the browser time to start the download in some hosts.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function DownloadPdfButton({
  disabled = false,
}: DownloadPdfButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const toast = useToast();

  const { brandId, analysisId, aiProvider } = useBrandAnalysisData();
  const { data: scan } = useScan();

  const { data: brands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,
    },
  });

  const { data: analysisData } = useGetAnalysis(brandId, analysisId, {
    query: {
      enabled: !!brandId && isSpecificAnalysisId(analysisId),
      select: ({ data }) => data?.analysis,
    },
  });

  const brand = brands?.find((b) => b.id === brandId);
  const brandName = brand?.name ?? 'Brand';
  const aiProviderLabel = getAIProviderLabel(aiProvider);
  const analysisDate = formatHumanDate(
    analysisData?.completedAt ?? analysisData?.createdAt,
  );

  const isReady = !!scan && !!brand && !!analysisData;

  const handleDownload = async () => {
    if (!isReady || !scan) return;
    setIsGenerating(true);
    try {
      await ensureNodeGlobalsForPdf();
      const [
        { renderChartImagesForPdf },
        { pdf },
        { default: BrandAnalysisPdfDocument },
      ] = await Promise.all([
        import('./renderChartImages'),
        import('@react-pdf/renderer'),
        import('./BrandAnalysisPdfDocument'),
      ]);

      const charts = await renderChartImagesForPdf(scan);
      const generatedAt = formatHumanDate(new Date());

      const blob = await pdf(
        <BrandAnalysisPdfDocument
          brandName={brandName}
          aiProviderLabel={aiProviderLabel}
          analysisDate={analysisDate}
          generatedAt={generatedAt}
          scan={scan}
          radarImage={charts.radar}
          mentionsProgressImage={charts.mentionsProgress}
        />,
      ).toBlob();

      const datePart =
        safeFilenamePart(analysisDate || generatedAt) || 'analysis';
      const filename = `brand-analysis_${safeFilenamePart(brandName) || 'brand'}_${safeFilenamePart(aiProviderLabel) || 'provider'}_${datePart}.pdf`;

      triggerBlobDownload(blob, filename);
    } catch (error) {
      console.error('Failed to generate brand analysis PDF', error);
      toast('Could not generate PDF. Please try again.', { variant: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant="secondary"
      onClick={handleDownload}
      disabled={disabled || !isReady || isGenerating}
      aria-label="Download brand analysis as PDF"
    >
      <DocumentDownloadIcon />
      {isGenerating ? 'Preparing PDF…' : 'Download PDF'}
    </Button>
  );
}
